import {
  Box3,
  type BufferGeometry,
  type Material,
  type Object3D,
  Vector3,
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';

import type { MotionEditorWorldAssetFormat } from './types';

type RenderableObject3D = Object3D & {
  isMesh?: boolean;
  isSkinnedMesh?: boolean;
  geometry?: BufferGeometry;
  material?: Material | Material[];
  castShadow?: boolean;
  receiveShadow?: boolean;
};

export async function loadWorldAssetObject(
  assetFormat: MotionEditorWorldAssetFormat,
  assetUrl: string,
  assetFile?: File,
) {
  if (assetFormat === 'glb') {
    if (assetFile) {
      return await parseWorldGltfArrayBuffer(await assetFile.arrayBuffer());
    }

    const gltf = await new GLTFLoader().loadAsync(assetUrl);
    return gltf.scene;
  }

  if (assetFile) {
    return new OBJLoader().parse(await assetFile.text());
  }

  return await new OBJLoader().loadAsync(assetUrl);
}

export function floorAndCenterImportedObject(object: Object3D) {
  object.updateMatrixWorld(true);
  const bounds = new Box3().setFromObject(object);
  if (!Number.isFinite(bounds.min.x) || bounds.isEmpty()) return;

  const center = bounds.getCenter(new Vector3());
  object.position.x -= center.x;
  object.position.z -= center.z;
  object.position.y -= bounds.min.y;
}

export function disposeObject3D(object: Object3D) {
  object.traverse((child) => {
    if (!isRenderableObject3D(child)) return;
    child.geometry?.dispose();
    disposeObjectMaterials(child.material);
  });
}

export function disposeObjectMaterials(
  material: Material | Material[] | undefined,
) {
  if (!material) return;
  const materials = Array.isArray(material) ? material : [material];
  for (const item of materials) {
    item.dispose();
  }
}

export function isRenderableObject3D(
  object: Object3D,
): object is RenderableObject3D {
  const maybeRenderable = object as RenderableObject3D;
  return Boolean(maybeRenderable.isMesh || maybeRenderable.isSkinnedMesh);
}

async function parseWorldGltfArrayBuffer(arrayBuffer: ArrayBuffer) {
  const loader = new GLTFLoader();
  const gltf = await new Promise<Awaited<ReturnType<GLTFLoader['parseAsync']>>>(
    (resolve, reject) => {
      loader.parse(
        arrayBuffer,
        '',
        (parsedGltf) => resolve(parsedGltf),
        (error) => reject(error),
      );
    },
  );

  return gltf.scene;
}
